import { makeAutoObservable, runInAction } from "mobx";
import { RootStore } from "../../shared/stores/RootStore";
import { ProjectApi } from "../services/ProjectApi";
import {
  Campaign,
  CreateCampaignRequest,
  UpdateCampaignRequest,
} from "../models/Campaign";
import { CampaignApi } from "../services/CampaignApi";
import { OsaWayApi } from "../../osaway/services/OsaWayApi";
import { compileAllPages } from "../../shared/utils/http-utils";
import { OsaWay } from "../../osaway/models/OsaWay";
import { HlaWay } from "../../hla/models/HlaWay";
import { HlaWayApi } from "../../hla/services/HlaWayApi";

export class CampaignStore {
  rootStore;
  campaigns: Campaign[] = [];
  selectedId?: string;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  resetState() {
    runInAction(() => {
      this.campaigns = [];
      this.selectedId = undefined;
    });
  }

  get selected() {
    if (this.selectedId) {
      return this.campaigns.find((c) => c.id === this.selectedId);
    }
    return undefined;
  }

  async fetchSelected(compaignId: string) {
    const response = await CampaignApi.get(compaignId);
    runInAction(() => {
      this.campaigns = [response.data];
      this.selectedId = response.data.id;
    });
  }

  async fetchByProject(projectId: string, cancelIf?: () => boolean) {
    const { data: page } = await ProjectApi.pageCampaigns(projectId, 100, 0);
    if (cancelIf?.()) return;

    runInAction(() => {
      this.campaigns = page.data;
    });
  }

  async create(request: CreateCampaignRequest) {
    const { data: campaign } = await CampaignApi.create(request);
    runInAction(() => {
      this.campaigns = [campaign, ...this.campaigns];
      this.selectedId = campaign.id;
    });
  }

  async update(campaignId: string, request: UpdateCampaignRequest) {
    const { data: campaign } = await CampaignApi.update(campaignId, request);
    runInAction(() => {
      this.campaigns = this.campaigns.map((p) =>
        p.id === campaignId ? campaign : p
      );
    });
  }

  setSelected(campaignId: string) {
    runInAction(() => {
      this.selectedId = campaignId;
    });
  }

  clearSelected() {
    runInAction(() => {
      this.selectedId = undefined;
    });
  }

  async delete(campaignId: string) {
    const osaWays: OsaWay[] = await compileAllPages(
      async (pageNumber) =>
        await CampaignApi.pageOsaWays(campaignId, [], 10, pageNumber)
    );

    for (const way of osaWays) {
      await OsaWayApi.delete(way.id);
    }

    const hlaWays: HlaWay[] = await compileAllPages(
      async (pageNumber) =>
        await CampaignApi.pageHlaWays(campaignId, 40, pageNumber)
    );

    for (const way of hlaWays) {
      await HlaWayApi.delete(way.id);
    }

    await CampaignApi.delete(campaignId);

    if (campaignId === this.selectedId) {
      this.clearSelected();
    }
    runInAction(() => {
      this.campaigns = this.campaigns.filter((c) => c.id !== campaignId);
    });
  }
}
